import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import itkLogo from "../assets/itk_logo.png";
import Header from "../components/Header";
import {
  Settings,
  Users,
  BookOpen,
  ChevronDown,
} from "lucide-react";

const PRIMARY_RED = "#C62828";
const PRIMARY_BLUE = "#1565C0";
const HEADER_HEIGHT = 72;
const HEADER_TOP_OFFSET = 12;
const MOBILE_HEADER_CLEARANCE = HEADER_HEIGHT + HEADER_TOP_OFFSET + 16;
const MOBILE_MENU_TOP = HEADER_HEIGHT + HEADER_TOP_OFFSET + 8;

const BG = "#f4f7fb";
const CARD_BG = "#ffffff";
const BORDER = "#dbe3ee";
const TEXT_DARK = "#0f172a";
const TEXT_MUTED = "#64748b";

const TYPO = {
  fontFamily:
    '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  weights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  sizes: {
    xs: 12,
    sm: 13,
    md: 14,
    lg: 16,
    xl: 24,
    xxl: 32,
  },
  lineHeights: {
    tight: 1.25,
    normal: 1.45,
    relaxed: 1.6,
  },
  letterSpacing: {
    tight: "-0.01em",
    normal: "0",
  },
};

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(768);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);

  const menuGroups = useMemo(
    () => [
      {
        key: "yonetim-paneli",
        title: "Yönetim Paneli",
        icon: Settings,
        children: [
          {
            label: "Kullanıcı Düzenleme",
            path: "/user-edit",
            icon: Users,
          },
          {
            label: "Müfredat Düzenleme",
            path: "/curriculum-edit",
            icon: BookOpen,
          },
        ],
      },
    ],
    []
  );

  const findPathMatch = (groups, pathname) => {
    for (const group of groups) {
      for (const child of group.children || []) {
        if (child.path === pathname) {
          return { groupKey: group.key, childLabel: child.label };
        }
      }
    }
    return null;
  };

  const initialMatch = findPathMatch(menuGroups, location.pathname);

  const [openGroups, setOpenGroups] = useState(() => ({
    "yonetim-paneli": true,
    ...(initialMatch?.groupKey ? { [initialMatch.groupKey]: true } : {}),
  }));

  useEffect(() => {
    const match = findPathMatch(menuGroups, location.pathname);
    if (match?.groupKey) {
      setOpenGroups((prev) => ({ ...prev, [match.groupKey]: true }));
    }
  }, [location.pathname, menuGroups]);

  useEffect(() => {
    if (!isMobile) setIsSidebarOpen(false);
  }, [isMobile]);

  const closeSidebar = () => setIsSidebarOpen(false);

  const isExpanded = isMobile ? true : isSidebarHovered;

  const sidebarStyle = isMobile
    ? {
        ...styles.mobileTopMenu,
        ...(isSidebarOpen ? styles.mobileTopMenuOpen : {}),
      }
    : {
        ...styles.sidebarDesktop,
        width: isExpanded ? "300px" : "96px",
      };

  const contentAreaStyle = isMobile
    ? {
        ...styles.contentArea,
        marginLeft: 0,
      }
    : {
        ...styles.contentArea,
        marginLeft: isExpanded ? "300px" : "96px",
        transition: "margin-left 0.28s ease",
      };

  const hasActiveDescendant = (node) => {
    if (node.path && node.path === location.pathname) return true;
    if (!node.children) return false;
    return node.children.some(hasActiveDescendant);
  };

  const renderChild = (child) => {
    const active = child.path === location.pathname;
    const ChildIcon = child.icon || Settings;

    return (
      <button
        key={`${child.label}-${child.path}`}
        style={{
          ...styles.subMenuItem,
          ...(active ? styles.subMenuItemActive : {}),
          paddingLeft: 14,
        }}
        onClick={() => {
          navigate(child.path);
          if (isMobile) closeSidebar();
        }}
      >
        <ChildIcon size={16} strokeWidth={2.1} style={styles.subMenuIcon} />
        <span style={styles.subMenuText}>{child.label}</span>
      </button>
    );
  };

  return (
    <div style={styles.page}>
      <Header
        isMobile={isMobile}
        isSidebarOpen={isSidebarOpen}
        onMenuToggle={() => setIsSidebarOpen((prev) => !prev)}
        mobileBgColor={PRIMARY_BLUE}
        mobileLeftCompact={true}
      />

      {isMobile && isSidebarOpen && (
        <div style={styles.mobileOverlay} onClick={closeSidebar} />
      )}

      <main
        style={{
          ...styles.main,
          ...(isMobile ? styles.mainMobile : {}),
        }}
      >
        <aside
          style={sidebarStyle}
          onMouseEnter={() => !isMobile && setIsSidebarHovered(true)}
          onMouseLeave={() => !isMobile && setIsSidebarHovered(false)}
        >
          {!isMobile && (
            <div style={styles.sidebarTop}>
              <div style={styles.logoContainer}>
                <img
                  src={itkLogo}
                  alt="Akademi Analitiği Logo"
                  style={styles.logo}
                />
              </div>
            </div>
          )}

          <div
            style={
              isMobile ? styles.mobileTopMenuScroll : styles.sidebarScroll
            }
          >
            {menuGroups.map((group) => {
              const active = hasActiveDescendant(group);
              const isOpen = !!openGroups[group.key];
              const GroupIcon = group.icon;

              return (
                <div key={group.key} style={styles.groupWrap}>
                  <button
                    style={{
                      ...styles.groupButton,
                      ...(active ? styles.groupButtonActive : {}),
                      justifyContent: isExpanded ? "space-between" : "center",
                    }}
                    onClick={() =>
                      setOpenGroups((prev) => ({
                        ...prev,
                        [group.key]: !prev[group.key],
                      }))
                    }
                    title={group.title}
                  >
                    <div style={styles.groupButtonLeft}>
                      <GroupIcon
                        size={20}
                        strokeWidth={2.1}
                        style={styles.groupIcon}
                      />
                      {isExpanded && (
                        <span style={styles.groupTitle}>{group.title}</span>
                      )}
                    </div>

                    {isExpanded && (
                      <ChevronDown
                        size={15}
                        strokeWidth={2.2}
                        style={{
                          ...styles.chevronIcon,
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      />
                    )}
                  </button>

                  {isExpanded && isOpen && (
                    <div style={styles.subMenuPanel}>
                      {group.children.map((child) => renderChild(child))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <section style={contentAreaStyle}>
          <div
            style={{
              ...styles.contentContainer,
              ...(isMobile ? styles.contentContainerMobile : {}),
            }}
          >
            <div style={styles.pageHero}>
              <div style={styles.heroGlow} />
              <div style={styles.heroBadge}>Yönetim</div>
              <div style={styles.containerHeader}>
                <h1 style={styles.pageTitle}>Yönetim Paneli</h1>
                <p style={styles.pageSubtitle}>
                  Yönetim işlemlerine hızlı erişim sağlayın.
                </p>
              </div>
            </div>

            <div
              style={{
                ...styles.managementGrid,
                ...(isMobile ? styles.managementGridMobile : {}),
              }}
            >
              <button
                style={{
                  ...styles.managementButton,
                  ...styles.managementButtonBlue,
                }}
                onClick={() => navigate("/user-edit")}
              >
                <div style={styles.buttonGlow} />
                <div style={styles.buttonInner}>
                  <div style={styles.iconWrap}>
                    <Users size={28} strokeWidth={2.1} color="#fff" />
                  </div>
                  <span style={styles.buttonText}>Kullanıcı Düzenleme</span>
                  <span style={styles.buttonSubtext}>
                    Kullanıcı kayıtlarını yönetin
                  </span>
                </div>
              </button>

              <button
                style={{
                  ...styles.managementButton,
                  ...styles.managementButtonBlue,
                }}
                onClick={() => navigate("/curriculum-edit")}
              >
                <div style={styles.buttonGlow} />
                <div style={styles.buttonInner}>
                  <div style={styles.iconWrap}>
                    <BookOpen size={28} strokeWidth={2.1} color="#fff" />
                  </div>
                  <span style={styles.buttonText}>Müfredat Düzenleme</span>
                  <span style={styles.buttonSubtext}>
                    Ders ve içerik yapısını güncelleyin
                  </span>
                </div>
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: BG,
    fontFamily: TYPO.fontFamily,
    display: "flex",
    flexDirection: "column",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    textRendering: "optimizeLegibility",
    overflowX: "hidden",
  },

  main: {
    flex: 1,
    display: "flex",
    position: "relative",
    padding: "24px",
    paddingTop: "24px",
    marginTop: `${MOBILE_HEADER_CLEARANCE}px`,
    background: BG,
    minWidth: 0,
  },

  mainMobile: {
    padding: "0",
    marginTop: `${MOBILE_HEADER_CLEARANCE}px`,
  },

  sidebarDesktop: {
    position: "fixed",
    top: "0",
    left: 0,
    bottom: 0,
    background: PRIMARY_BLUE,
    color: "#fff",
    borderRight: "1px solid rgba(255,255,255,0.08)",
    boxShadow:
      "24px 0 48px rgba(15, 23, 42, 0.24), 8px 0 18px rgba(15, 23, 42, 0.14)",
    transition: "width 0.28s ease",
    zIndex: 40,
    overflow: "hidden",
  },

  mobileTopMenu: {
    position: "fixed",
    top: `${MOBILE_MENU_TOP}px`,
    left: "16px",
    right: "16px",
    background: PRIMARY_BLUE,
    color: "#fff",
    borderRadius: 16,
    boxShadow:
      "0 18px 40px rgba(15, 23, 42, 0.22), 0 6px 18px rgba(15, 23, 42, 0.14)",
    zIndex: 70,
    overflow: "hidden",
    opacity: 0,
    transform: "translateY(-12px)",
    pointerEvents: "none",
    transition: "all 0.22s ease",
  },

  mobileTopMenuOpen: {
    opacity: 1,
    transform: "translateY(0)",
    pointerEvents: "auto",
  },

  sidebarTop: {
    minHeight: "72px",
    padding: "12px 10px",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottom: "1px solid rgba(255,255,255,0.14)",
    background: PRIMARY_BLUE,
  },

  logoContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "8px 0",
  },

  logo: {
    width: "48px",
    height: "48px",
    objectFit: "contain",
    borderRadius: "14px",
    background: "#ffffff",
    padding: "6px",
  },

  sidebarScroll: {
    height: "calc(100% - 72px)",
    overflowY: "auto",
    padding: "12px 10px 18px",
  },

  mobileTopMenuScroll: {
    padding: "12px",
  },

  groupWrap: {
    marginBottom: 10,
  },

  groupButton: {
    width: "100%",
    minHeight: 48,
    border: "none",
    background: "transparent",
    borderRadius: 12,
    color: "#ffffff",
    padding: "0 12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease",
  },

  groupButtonActive: {
    background: "rgba(255,255,255,0.16)",
    color: "#ffffff",
    boxShadow: "none",
  },

  groupButtonLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  groupIcon: {
    flexShrink: 0,
    color: "#ffffff",
  },

  groupTitle: {
    fontSize: TYPO.sizes.md,
    fontWeight: TYPO.weights.semibold,
    letterSpacing: TYPO.letterSpacing.normal,
    lineHeight: TYPO.lineHeights.tight,
  },

  chevronIcon: {
    color: "#ffffff",
    transition: "transform 0.2s ease",
    flexShrink: 0,
  },

  subMenuPanel: {
    marginTop: 8,
    marginLeft: 8,
    paddingLeft: 10,
    borderLeft: "2px solid rgba(255,255,255,0.18)",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  subMenuItem: {
    minHeight: 40,
    border: "none",
    borderRadius: 10,
    background: "transparent",
    color: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 10,
    textAlign: "left",
    fontSize: TYPO.sizes.sm,
    fontWeight: TYPO.weights.medium,
    letterSpacing: TYPO.letterSpacing.normal,
    lineHeight: TYPO.lineHeights.normal,
    transition: "all 0.2s ease",
  },

  subMenuItemActive: {
    background: "#ffffff",
    color: PRIMARY_BLUE,
    fontWeight: TYPO.weights.semibold,
  },

  subMenuIcon: {
    flexShrink: 0,
  },

  subMenuText: {
    fontSize: TYPO.sizes.sm,
    fontWeight: TYPO.weights.medium,
    lineHeight: TYPO.lineHeights.normal,
    letterSpacing: TYPO.letterSpacing.normal,
  },

  contentArea: {
    flex: 1,
    width: "100%",
    maxWidth: "100%",
    margin: 0,
    background: "transparent",
    borderRadius: 0,
    padding: 0,
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minWidth: 0,
    overflowX: "hidden",
  },

  contentContainer: {
    width: "100%",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
    borderRadius: 24,
    padding: "24px",
    minHeight: "calc(100vh - 168px)",
    boxShadow:
      "0 20px 50px rgba(15, 23, 42, 0.10), 0 8px 22px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255,255,255,0.75)",
    border: `1px solid ${BORDER}`,
    boxSizing: "border-box",
    overflow: "hidden",
    position: "relative",
  },

  contentContainerMobile: {
    padding: "14px 12px 12px",
    borderRadius: 16,
    minHeight: "calc(100vh - 150px)",
  },

  pageHero: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 20,
    padding: "20px 20px 18px",
    marginBottom: 18,
    background:
      "linear-gradient(135deg, rgba(21,101,192,0.08) 0%, rgba(255,255,255,0.95) 65%)",
    border: "1px solid rgba(21,101,192,0.10)",
  },

  heroGlow: {
    position: "absolute",
    top: "-60px",
    right: "-40px",
    width: 180,
    height: 180,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(21,101,192,0.14) 0%, rgba(21,101,192,0) 72%)",
    pointerEvents: "none",
  },

  heroBadge: {
    position: "relative",
    zIndex: 1,
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(21,101,192,0.08)",
    color: PRIMARY_BLUE,
    border: "1px solid rgba(21,101,192,0.12)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.01em",
    marginBottom: 10,
  },

  containerHeader: {
    position: "relative",
    zIndex: 1,
  },

  pageTitle: {
    margin: 0,
    fontSize: TYPO.sizes.xl,
    fontWeight: TYPO.weights.bold,
    color: PRIMARY_BLUE,
    lineHeight: TYPO.lineHeights.tight,
    letterSpacing: TYPO.letterSpacing.tight,
  },

  pageSubtitle: {
    margin: "8px 0 0 0",
    fontSize: TYPO.sizes.md,
    color: TEXT_MUTED,
    lineHeight: TYPO.lineHeights.relaxed,
  },

  managementGrid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
    alignItems: "stretch",
  },

  managementGridMobile: {
    gridTemplateColumns: "1fr",
    gap: 12,
  },

  managementButton: {
    position: "relative",
    width: "100%",
    minHeight: 180,
    borderRadius: 22,
    border: "none",
    color: "#ffffff",
    padding: "24px 18px",
    fontSize: TYPO.sizes.md,
    fontWeight: TYPO.weights.semibold,
    fontFamily: TYPO.fontFamily,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    overflow: "hidden",
    boxShadow:
      "0 18px 34px rgba(15, 23, 42, 0.14), inset 0 1px 0 rgba(255,255,255,0.18)",
  },

  managementButtonBlue: {
    background: "linear-gradient(135deg, #1565C0 0%, #1e88e5 100%)",
  },

  buttonGlow: {
    position: "absolute",
    top: "-50px",
    right: "-20px",
    width: 170,
    height: 170,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0) 72%)",
    pointerEvents: "none",
  },

  buttonInner: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    textAlign: "center",
  },

  iconWrap: {
    width: 60,
    height: 60,
    borderRadius: 18,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.16)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)",
  },

  buttonText: {
    fontSize: TYPO.sizes.lg,
    fontWeight: TYPO.weights.bold,
    lineHeight: TYPO.lineHeights.normal,
    color: "#ffffff",
  },

  buttonSubtext: {
    fontSize: TYPO.sizes.sm,
    fontWeight: TYPO.weights.medium,
    lineHeight: TYPO.lineHeights.relaxed,
    color: "rgba(255,255,255,0.88)",
    maxWidth: 220,
  },

  mobileOverlay: {
    position: "fixed",
    top: `${MOBILE_MENU_TOP}px`,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(15, 23, 42, 0.18)",
    zIndex: 65,
  },
};

export default AdminDashboard;